import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import {
  Observable,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import {
  ClientTimingStepResult,
  WizardClientMatter,
} from '../../../../models/request-wizard.models';
import { ClientDto, ClientService } from '../../../../services/client.service';
import { ExternalMatterDto, MatterService } from '../../../../services/matter.service';

function dateRangeValidator(group: AbstractControl): ValidationErrors | null {
  const start = group.get('startDate')?.value;
  const end = group.get('endDate')?.value;
  if (!start || !end) return null;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (isNaN(s) || isNaN(e)) return null;
  return s <= e ? null : { dateRange: true };
}

/** Strips an optional leading `#` (e.g. "#00036") and surrounding whitespace. */
function normalizeSearchTerm(raw: string | null | undefined): string {
  return (raw || '').trim().replace(/^#+\s*/, '').trim();
}

const MIN_CLIENT_QUERY_LENGTH = 3;

/** Number of client results to fetch for the client search typeahead. */
const CLIENT_SEARCH_PAGE_SIZE = 25;

/** Matches a fully-qualified client-matter number, e.g. `123456-7890`. */
const CLIENT_MATTER_NUMBER_PATTERN = /^\d{4,10}-\d{4,10}$/;

/** Upper bound of matters loaded per client for client-side filter/paging. */
const MATTER_LOAD_SIZE = 1000;

@Component({
  selector: 'app-client-timing-step',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './client-timing-step.component.html',
  styleUrls: ['./client-timing-step.component.scss'],
})
export class ClientTimingStepComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private clientService = inject(ClientService);
  private matterService = inject(MatterService);

  @Input() initialResult: ClientTimingStepResult | null = null;
  @Output() resultChange = new EventEmitter<ClientTimingStepResult>();

  form!: FormGroup;

  // Client search (step 1)
  selectedClient: ClientDto | null = null;
  clientSuggestions: ClientDto[] = [];
  isLoadingClients = false;
  clientsNotFound = false;
  /** Error shown when a pasted client-matter number resolves to no matter. */
  clientMatterError: string | null = null;

  // Matters for the selected client (step 2). All of the client's matters are
  // loaded once, then filtered (by name or number) and paged client-side.
  allMatters: ExternalMatterDto[] = [];
  matters: ExternalMatterDto[] = [];
  isLoadingMatters = false;
  mattersNotFound = false;
  /** Error shown when a client-matter number in the matter filter resolves to nothing. */
  matterError: string | null = null;
  matterTotalCount = 0;
  matterPageIndex = 0;
  matterPageSize = 10;
  matterPageSizeOptions = [10, 25, 50];
  readonly matterColumns = ['matter', 'action'];

  selectedClientMatters: WizardClientMatter[] = [];

  private destroy$ = new Subject<void>();
  private clientQuery$ = new Subject<string>();
  private matterReload$ = new Subject<void>();
  private matterCmnQuery$ = new Subject<string>();

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        clientSearch: [''],
        matterFilter: [''],
        startDate: [null],
        endDate: [null],
      },
      { validators: dateRangeValidator },
    );

    if (this.initialResult) {
      this.selectedClientMatters = this.initialResult.clientMatters ?? [];
      this.form.patchValue(
        {
          startDate: this.initialResult.startDate,
          endDate: this.initialResult.endDate,
        },
        { emitEvent: false },
      );
    }

    this.setupClientTypeahead();
    this.setupMatterTable();

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emit());

    this.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupClientTypeahead(): void {
    this.clientQuery$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        switchMap((query) => {
          const trimmed = normalizeSearchTerm(query);
          this.clientMatterError = null;

          // Pasting a full client-matter number resolves its client + matter.
          if (CLIENT_MATTER_NUMBER_PATTERN.test(trimmed)) {
            this.isLoadingClients = true;
            this.clientSuggestions = [];
            this.clientsNotFound = false;
            return this.lookupClientMatter(trimmed).pipe(
              map((res) => ({ kind: 'cmn' as const, cmn: trimmed, res })),
            );
          }

          if (trimmed.length < MIN_CLIENT_QUERY_LENGTH) {
            this.isLoadingClients = false;
            return of({ kind: 'clients' as const, clients: [] as ClientDto[] });
          }

          // A purely numeric query is likely a client number. The search term
          // only matches client names, so pair it with an exact client-number
          // lookup and surface that client at the top of the results.
          if (/^\d+$/.test(trimmed)) {
            this.isLoadingClients = true;
            return forkJoin({
              exact: this.clientService
                .getClient(trimmed)
                .pipe(catchError(() => of(null))),
              search: this.clientService
                .searchClients(trimmed, undefined, undefined, CLIENT_SEARCH_PAGE_SIZE, 1)
                .pipe(catchError(() => of(null))),
            }).pipe(
              map(({ exact, search }) => {
                const clients = search?.clients ? [...search.clients] : [];
                if (
                  exact &&
                  !clients.some((c) => c.clientNumber === exact.clientNumber)
                ) {
                  clients.unshift(exact);
                }
                return { kind: 'clients' as const, clients };
              }),
            );
          }

          this.isLoadingClients = true;
          return this.clientService
            .searchClients(trimmed, undefined, undefined, CLIENT_SEARCH_PAGE_SIZE, 1)
            .pipe(
              map((resp) => ({
                kind: 'clients' as const,
                clients: resp.clients,
              })),
              catchError(() =>
                of({ kind: 'clients' as const, clients: [] as ClientDto[] }),
              ),
            );
        }),
      )
      .subscribe((result) => {
        this.isLoadingClients = false;
        if (result.kind === 'cmn') {
          if (result.res) {
            this.showResolvedMatter(result.res.client, result.res.matter);
          } else {
            this.clientMatterError = `No matter found for "${result.cmn}". Check the client-matter number and try again.`;
          }
        } else {
          this.clientSuggestions = result.clients;
          this.clientsNotFound = result.clients.length === 0;
        }
      });
  }

  /**
   * Resolves a client-matter number to its matter and owning client. Reuses the
   * already-selected client when the matter belongs to it; otherwise fetches the
   * owning client. Resolves to null when the matter isn't found.
   */
  private lookupClientMatter(
    cmn: string,
  ): Observable<{ matter: ExternalMatterDto; client: ClientDto } | null> {
    return this.matterService.getMatter(cmn).pipe(
      switchMap((matter) => {
        if (
          this.selectedClient &&
          matter.clientNumber === this.selectedClient.clientNumber
        ) {
          return of({ matter, client: this.selectedClient as ClientDto });
        }
        return this.clientService
          .getClientsFromList([matter.clientNumber])
          .pipe(
            map((resp) => ({
              matter,
              client:
                resp.clients[0] ??
                ({
                  clientNumber: matter.clientNumber,
                  clientName: matter.clientNumber,
                } as ClientDto),
            })),
          );
      }),
      catchError(() => of(null)),
    );
  }

  /**
   * Selects (or switches to) the matter's client and shows just that matter in
   * the table. The matter number (not the full client-matter number) is left in
   * the matter filter, so editing/clearing it reverts to a plain matter search
   * — clearing shows all matters for the still-selected client.
   */
  private showResolvedMatter(client: ClientDto, matter: ExternalMatterDto): void {
    this.selectedClient = client;
    this.clientSuggestions = [];
    this.clientsNotFound = false;
    this.clientMatterError = null;
    this.matterError = null;
    this.matterPageIndex = 0;
    // Leave the full client-matter number in the matter filter; the client-side
    // filter still matches it (and a bare matter number) against the matters.
    this.form.patchValue(
      { clientSearch: '', matterFilter: matter.clientMatterNumber },
      { emitEvent: false },
    );
    // Load the client's matters, then the filter narrows to the matched one.
    this.matterReload$.next();
  }

  private setupMatterTable(): void {
    // Loads ALL of the selected client's matters; filtering (by matter name or
    // number) and paging are then done client-side so both reliably match.
    this.matterReload$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          if (!this.selectedClient) return of(null);
          this.isLoadingMatters = true;
          this.mattersNotFound = false;
          return this.matterService
            .searchMatters(
              undefined,
              undefined,
              this.selectedClient.clientNumber,
              undefined,
              undefined,
              MATTER_LOAD_SIZE,
              1,
            )
            .pipe(catchError(() => of(null)));
        }),
      )
      .subscribe((response) => {
        this.isLoadingMatters = false;
        this.allMatters = response?.matters ?? [];
        this.applyMatterFilter();
      });

    // A client-matter number in the matter filter resolves directly, switching
    // the selected client automatically when it belongs to a different client.
    this.matterCmnQuery$
      .pipe(
        takeUntil(this.destroy$),
        switchMap((cmn) => {
          this.isLoadingMatters = true;
          this.matterError = null;
          return this.lookupClientMatter(cmn).pipe(map((res) => ({ cmn, res })));
        }),
      )
      .subscribe(({ cmn, res }) => {
        if (res) {
          this.showResolvedMatter(res.client, res.matter);
        } else {
          this.isLoadingMatters = false;
          this.matterError = `No matter found for "${cmn}". Check the client-matter number and try again.`;
        }
      });

    this.form
      .get('matterFilter')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        const trimmed = normalizeSearchTerm(value);
        this.matterError = null;
        // A full client-matter number for a *different* client switches clients;
        // a number for the current client (or a bare matter number) just filters
        // the loaded matters client-side.
        if (
          CLIENT_MATTER_NUMBER_PATTERN.test(trimmed) &&
          trimmed.split('-')[0] !== this.selectedClient?.clientNumber
        ) {
          this.matterCmnQuery$.next(trimmed);
          return;
        }
        if (!this.selectedClient) return;
        this.matterPageIndex = 0;
        this.applyMatterFilter();
      });
  }

  /**
   * Filters the loaded matters by the current term (matched against matter name
   * and client-matter number) and slices the active page. With an empty term,
   * all of the client's matters are shown.
   */
  private applyMatterFilter(): void {
    const term = normalizeSearchTerm(
      this.form.get('matterFilter')?.value,
    ).toLowerCase();
    const filtered = term
      ? this.allMatters.filter(
          (m) =>
            (m.matterName || '').toLowerCase().includes(term) ||
            (m.clientMatterNumber || '').toLowerCase().includes(term),
        )
      : this.allMatters;
    this.matterTotalCount = filtered.length;
    this.mattersNotFound = filtered.length === 0;
    const start = this.matterPageIndex * this.matterPageSize;
    this.matters = filtered.slice(start, start + this.matterPageSize);
  }

  onClientInput(event: Event): void {
    this.clientQuery$.next((event.target as HTMLInputElement).value);
  }

  onClientBlur(): void {
    setTimeout(() => (this.clientSuggestions = []), 200);
  }

  selectClient(client: ClientDto): void {
    this.selectedClient = client;
    this.clientSuggestions = [];
    this.clientsNotFound = false;
    this.clientMatterError = null;
    this.matters = [];
    this.matterTotalCount = 0;
    this.matterPageIndex = 0;
    this.form.patchValue({ matterFilter: '' }, { emitEvent: false });
    this.matterReload$.next();
  }

  onMatterPage(event: PageEvent): void {
    this.matterPageIndex = event.pageIndex;
    this.matterPageSize = event.pageSize;
    this.applyMatterFilter();
  }

  isMatterAdded(clientMatterNumber: string): boolean {
    return this.selectedClientMatters.some(
      (m) => m.clientMatterNumber === clientMatterNumber,
    );
  }

  addMatter(matter: ExternalMatterDto): void {
    if (!this.selectedClient || this.isMatterAdded(matter.clientMatterNumber)) {
      return;
    }
    const newMatter: WizardClientMatter = {
      clientNumber: this.selectedClient.clientNumber,
      clientName: this.selectedClient.clientName,
      matterNumber:
        matter.clientMatterNumber.split('-')[1] || matter.clientMatterNumber,
      matterName: matter.matterName,
      clientMatterNumber: matter.clientMatterNumber,
    };
    this.selectedClientMatters = [...this.selectedClientMatters, newMatter];
    this.form.markAsDirty();
    this.emit();
  }

  removeMatter(matter: WizardClientMatter): void {
    this.selectedClientMatters = this.selectedClientMatters.filter(
      (m) => m.clientMatterNumber !== matter.clientMatterNumber,
    );
    this.emit();
  }

  /** Drops the selected client so a different one can be searched. Chips remain. */
  clearSelectedClient(): void {
    this.selectedClient = null;
    this.allMatters = [];
    this.matters = [];
    this.matterTotalCount = 0;
    this.matterPageIndex = 0;
    this.mattersNotFound = false;
    this.isLoadingMatters = false;
    this.clientMatterError = null;
    this.form.patchValue(
      { clientSearch: '', matterFilter: '' },
      { emitEvent: false },
    );
    this.clientQuery$.next('');
  }

  clearClientMatter(): void {
    this.selectedClientMatters = [];
    this.clearSelectedClient();
    this.form.markAsDirty();
    this.emit();
  }

  /** Human-readable list of what's still required on this step. */
  getValidationIssues(): string[] {
    const issues: string[] = [];
    if (this.form?.hasError('dateRange')) {
      issues.push('End date must be on or after the start date.');
    }
    return issues;
  }

  private emit(): void {
    this.resultChange.emit({
      clientMatters: this.selectedClientMatters,
      startDate: this.form.get('startDate')?.value || null,
      endDate: this.form.get('endDate')?.value || null,
    });
  }
}
